import React, { useState } from 'react'
import { Box, makeStyles, Paper, Switch, Typography } from '@material-ui/core'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'
import { toast } from 'utils/notify'
import * as yup from 'yup'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'

import { updateConferenceById } from 'store/conference'
import ProgressButton from 'components/ProgressButton'
import ErrorMessage from 'components/ErrorMessage'
import ConfirmationModal from 'components/ConfirmationModal'

const validationSchema = yup.object().shape({
  enableResources: yup.boolean(),
  resourceContent: yup.string().when('enableResources', {
    is: true,
    then: yup.string().trim().required('Resource Content is required'),
    otherwise: yup.string()
  })
})

const ResourcesForm = ({ conference }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false)
  const [pendingEnableState, setPendingEnableState] = useState(null)

  const initialValues = {
    enableResources: conference?.enableResources || false,
    resourceContent: conference?.resourceContent || ''
  }

  const handleToggleChange = (setFieldValue, currentValue) => {
    if (currentValue && !pendingEnableState && currentValue !== !conference?.enableResources) {
      // If we're trying to disable and it's currently enabled, show confirmation dialog
      setShowDisableConfirmation(true)
      setPendingEnableState(false)
    } else {
      // Otherwise, just enable it directly
      setFieldValue('enableResources', !currentValue)
    }
  }

  const handleConfirmDisable = async (setFieldValue) => {
    setFieldValue('enableResources', false)
    setShowDisableConfirmation(false)
    setPendingEnableState(null)

    // Since we're disabling the feature, we don't need to validate the content
    try {
      setLoading(true)
      await dispatch(
        updateConferenceById(conference.id, { enableResources: false, resourceContent: '' })
      )
      toast.success('Resources module disabled.')
    } catch (error) {
      toast.error('Could not disable resources module.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelDisable = () => {
    setShowDisableConfirmation(false)
    setPendingEnableState(null)
  }

  const handleSubmit = async (values) => {
    if (!conference?.id) {
      toast.error('Conference ID is missing. Reload and try again.')
      return
    }

    const payload = {
      enableResources: values.enableResources,
      resourceContent: values.enableResources ? values.resourceContent : ''
    }

    try {
      setLoading(true)
      await dispatch(updateConferenceById(conference.id, payload))
      toast.success('Resources settings updated.')
    } catch (error) {
      toast.error('Could not update resources settings.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Paper className={classes.section}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize>
        {({ setFieldValue, values, errors, touched }) => (
          <Form>
            <Box
              className={classes.sectionTitle}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
              <Typography variant="h6" color="secondary" style={{ fontWeight: 'bold' }}>
                Resources
              </Typography>

              <Switch
                color="primary"
                name="enableResources"
                onChange={() => handleToggleChange(setFieldValue, values.enableResources)}
                checked={values.enableResources}
              />
            </Box>

            {values.enableResources ? (
              <Box className={classes.resourceContainer}>
                <label className={classes.label}>Resource Content</label>
                <TipTapEditor
                  hasPlaceholders={false}
                  value={values.resourceContent}
                  onChange={(value) => setFieldValue('resourceContent', value)}
                  placeholder="Write resource content here..."
                />
                <ErrorMessage message={touched?.resourceContent && errors?.resourceContent} />

                <Box className={classes.actions}>
                  <ProgressButton
                    type="submit"
                    label="Save Changes"
                    inProgress={loading}
                    disabled={loading}
                    color="primary"
                    variant="contained"
                  />
                </Box>
              </Box>
            ) : (
              <Typography style={{ textAlign: 'center' }}>
                This module is currently disabled. Toggle the switch to enable it.
              </Typography>
            )}
            <ConfirmationModal
              open={showDisableConfirmation}
              dialogTitle="Disable Resources Module"
              dialogText="Are you sure you want to disable the resources module? Any content will be preserved but hidden from users."
              closeModal={handleCancelDisable}
              handleConfirm={() => handleConfirmDisable(setFieldValue)}
            />
          </Form>
        )}
      </Formik>
    </Paper>
  )
}

const useStyles = makeStyles({
  section: {
    marginBottom: '24px',
    padding: '18px 24px',
    borderRadius: '8px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    backgroundColor: '#ffffff'
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px'
  },
  resourceContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '15px',
    fontWeight: 500,
    marginBottom: '8px'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px'
  }
})

export default ResourcesForm
